import { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import avialiteService from '../services/avialiteService';
import havvaService from '../services/havvaService';
import ellianceService from '../services/ellianceService';
import { resolveFarmerId } from '../services/farmIdentity';

/**
 * useIotSummary - data for the two IoT boxes of the home dashboard.
 *
 * 1. Device counts: how many LEDs (Avialite), cameras (HAVVA) and sensors
 *    (Elliance) are registered under the signed-in account, for the selected
 *    farm or for every farm ('all'). They come from the middleware's
 *    device-list service, which only says what is registered - nothing here
 *    knows whether a device is online. A selected farm is asked for under
 *    the farmer id of its middleware farm record (see farmIdentity), 'all'
 *    under the signed-in user's id.
 * 2. Recent activity: the 3 newest rows of the Supabase table
 *    havva_analysis_history (plant photos analysed by HAVVA).
 *
 * The middleware is fragile, so the device lists are asked for with care:
 *  - one request at a time, never in parallel (LEDs, then cameras, then
 *    sensors; a second view waits for the first to finish);
 *  - every answer - a failure too - is kept in memory for 5 minutes per
 *    user + farm, so coming back to the home page asks nothing again and a
 *    failed request is not repeated on its own;
 *  - one load is shared by everything that shows the same user + farm;
 *  - a load nobody is looking at any more (the farm was changed, the page
 *    was left) stops before its next request.
 * Only the three device lists are requested. Sensor readings and sensor
 * status are never requested from here.
 */

// How long a device-list answer (or failure) is reused. Memory only: a page
// reload clears it.
const DEVICE_CACHE_MS = 5 * 60 * 1000;

const ACTIVITY_TABLE = 'havva_analysis_history';
const ACTIVITY_COLUMNS = 'id, farm_id, camera_id, analyzed_at, healthy_percentage, unhealthy_percentage, analysis_json';
const ACTIVITY_LIMIT = 3;
const ACTIVITY_TITLE = 'Plant photo analysed';

// Same rule as the three services: empty, 'all' and 'ALL' mean every farm
const isAllFarms = (farmId) =>
  farmId === null || farmId === undefined || farmId === '' || farmId === 'all' || farmId === 'ALL';

const toFarmKey = (farmId) => (isAllFarms(farmId) ? 'all' : String(farmId));

// The three lists, in the order they are requested. Each service answers
// { success: true, data } or { success: false, message, ... } and does not
// throw; data is the middleware's reply, which is a list only when all is well.
// farmerId is the id the request goes out under; userId the signed-in user.
const DEVICE_SOURCES = [
  {
    name: 'leds',
    list: (farmId, farmerId) => avialiteService.listLEDs(farmId, farmerId)
  },
  {
    name: 'cameras',
    list: (farmId, farmerId, userId) => {
      // Lists cached for another user (logout and login on the same tab) are forgotten
      havvaService.setCameraCacheOwner(userId);
      return havvaService.listCamerasCached(farmId, farmerId);
    }
  },
  {
    name: 'sensors',
    list: (farmId, farmerId) => ellianceService.listSensors(farmId, farmerId)
  }
];

const LOADING_DEVICE = { status: 'loading', count: null };

const LOADING_DEVICES = {
  leds: LOADING_DEVICE,
  cameras: LOADING_DEVICE,
  sensors: LOADING_DEVICE
};

const LOADING_ACTIVITY = { status: 'loading', items: [] };

// `${userId}__${farmKey}` -> {
//   devices:   { leds, cameras, sensors }, each { status, count, at } with
//              status 'idle' (not asked), 'loading' (asked or waiting its
//              turn), 'ok' or 'error'
//   running:   a load of this entry is queued or under way
//   listeners: the mounted hooks showing this entry
// }
const summaryCache = new Map();

// Every load is chained onto this promise, so only one middleware request
// from the home page is under way at any time.
let requestQueue = Promise.resolve();

const isFresh = (device, now) =>
  (device.status === 'ok' || device.status === 'error') && now - device.at < DEVICE_CACHE_MS;

/**
 * Number of devices in a list answer; null when the request failed or the
 * reply is not a list (never 0 for "unknown"). With a farm selected, a
 * device that names another farm is left out, as the device pages do.
 */
const countDevices = (result, farmKey) => {
  if (!result || result.success !== true || !Array.isArray(result.data)) return null;
  if (farmKey === 'all') return result.data.length;
  return result.data.filter((device) => {
    const farmId = device && typeof device === 'object' ? device.farmId : null;
    return farmId === null || farmId === undefined || farmId === '' || String(farmId) === farmKey;
  }).length;
};

const notify = (entry) => {
  [...entry.listeners].forEach((listener) => listener());
};

// Ask for the lists of one entry that are still wanted, one after the other.
// Never throws.
const runLoad = async (entry, userId, farmKey) => {
  const farmId = farmKey === 'all' ? 'ALL' : farmKey;
  // A farm's lists are asked for under the farmer id of its middleware farm
  // record (one lookup per farm, cached by farmIdentity); 'all' keeps the
  // user's id. Nothing is looked up when nobody shows this entry any more.
  const farmerId = farmKey === 'all' || entry.listeners.size === 0
    ? userId
    : await resolveFarmerId(farmKey, userId);

  for (const source of DEVICE_SOURCES) {
    if (entry.devices[source.name].status !== 'loading') continue;

    // Nobody shows this user + farm any more: ask nothing further. What was
    // already answered is kept; the rest is asked the next time it is shown.
    if (entry.listeners.size === 0) {
      entry.devices[source.name] = { status: 'idle', count: null, at: 0 };
      continue;
    }

    let count = null;
    try {
      count = countDevices(await source.list(farmId, farmerId, userId), farmKey);
    } catch (error) {
      count = null;
    }
    entry.devices[source.name] = { status: count === null ? 'error' : 'ok', count, at: Date.now() };
    notify(entry);
  }

  entry.running = false;
};

/**
 * The cache entry of a user + farm, with a load queued for whatever it lacks
 * (nothing when every answer is younger than 5 minutes, or when a load is
 * already queued or running).
 */
const ensureLoaded = (userId, farmKey) => {
  const key = `${userId}__${farmKey}`;
  let entry = summaryCache.get(key);
  if (!entry) {
    entry = {
      devices: {
        leds: { status: 'idle', count: null, at: 0 },
        cameras: { status: 'idle', count: null, at: 0 },
        sensors: { status: 'idle', count: null, at: 0 }
      },
      running: false,
      listeners: new Set()
    };
    summaryCache.set(key, entry);
  }

  if (!entry.running) {
    const now = Date.now();
    let wanted = false;
    DEVICE_SOURCES.forEach(({ name }) => {
      if (!isFresh(entry.devices[name], now)) {
        entry.devices[name] = { status: 'loading', count: null, at: 0 };
        wanted = true;
      }
    });
    if (wanted) {
      entry.running = true;
      requestQueue = requestQueue
        .then(() => runLoad(entry, userId, farmKey))
        .catch(() => {
          entry.running = false;
        });
    }
  }

  return entry;
};

// What the page gets for one device type: { status: 'loading' | 'ok' | 'error', count }.
// An answer stays on screen for as long as the page is open; its age only
// matters the next time the page is opened (see ensureLoaded).
const toDeviceState = (device) => {
  if (device.status === 'ok') return { status: 'ok', count: device.count };
  if (device.status === 'error') return { status: 'error', count: null };
  return LOADING_DEVICE;
};

const readDevices = (userId, farmKey) => {
  const entry = userId ? summaryCache.get(`${userId}__${farmKey}`) : null;
  if (!entry) return LOADING_DEVICES;
  // The page is being opened (nothing shows this entry yet): an answer older
  // than 5 minutes is about to be asked again, so it reads as loading instead
  // of showing the old count for a moment.
  const opening = entry.listeners.size === 0;
  const now = Date.now();
  const read = (device) => (opening && !isFresh(device, now) ? LOADING_DEVICE : toDeviceState(device));
  return {
    leds: read(entry.devices.leds),
    cameras: read(entry.devices.cameras),
    sensors: read(entry.devices.sensors)
  };
};

// ── Recent activity ─────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-07-15T10:00:00" / "2026-07-15 10:00:00" - a date-time with no zone
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * A timestamp as a date, day first: "16 Jul 2026" (the browser's time zone).
 * null when the value does not parse. A date-time without a zone is read as
 * UTC, as the HAVVA page does.
 */
const formatDayFirst = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  let input = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    input = ZONELESS_DATETIME.test(trimmed) ? `${trimmed.replace(' ', 'T')}Z` : trimmed;
  }
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

const NO_VALUE_TEXT = /^(n\/?a|n\.a\.?|null|none|undefined|nan|-+)$/i;

const toRealText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' || NO_VALUE_TEXT.test(trimmed) ? null : trimmed;
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The AI's confidence as a whole percentage. analysis_json keeps it as a
 * fraction 0-1 (0.65 -> 65); a value that is already a percentage is
 * accepted, anything unclear is left out (null).
 */
const toConfidencePercent = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  if (value <= 1) return Math.round(value * 100);
  if (value <= 1.01) return 100;
  if (value <= 1.5 || value > 100) return null;
  return Math.round(value);
};

// "Unhealthy · 65% confidence" from analysis_json.health; null without a label
const describeHealth = (analysisJson) => {
  let stored = analysisJson;
  if (typeof stored === 'string') {
    try {
      stored = JSON.parse(stored);
    } catch (error) {
      return null;
    }
  }
  const health = stored && typeof stored === 'object' ? stored.health : null;
  if (!health || typeof health !== 'object') return null;

  const label = toRealText(health.label) || toRealText(health.status);
  if (!label) return null;
  const text = label.charAt(0).toUpperCase() + label.slice(1);
  const confidence = toConfidencePercent(health.confidence);
  return confidence === null ? text : `${text} · ${confidence}% confidence`;
};

// The short result of one analysis, or null when the row holds none:
// the health assessment (new HAVVA format), else the healthy / unhealthy
// percentage (old format).
const describeResult = (row) => {
  const health = describeHealth(row.analysis_json);
  if (health) return health;
  const healthy = toFiniteNumber(row.healthy_percentage);
  if (healthy !== null) return `${healthy.toFixed(1)}% healthy`;
  const unhealthy = toFiniteNumber(row.unhealthy_percentage);
  if (unhealthy !== null) return `${unhealthy.toFixed(1)}% unhealthy`;
  return null;
};

// One havva_analysis_history row as the two lines of text the page shows
const toActivityItem = (row, index) => {
  const source = row && typeof row === 'object' ? row : {};
  const result = describeResult(source);
  const cameraId = typeof source.camera_id === 'number' ? String(source.camera_id) : toRealText(source.camera_id);
  const date = formatDayFirst(source.analyzed_at);
  return {
    id: source.id ?? `row-${index}`,
    title: result ? `${ACTIVITY_TITLE}: ${result}` : ACTIVITY_TITLE,
    detail: [cameraId ? `Camera ${cameraId}` : null, date].filter(Boolean).join(' · ')
  };
};

/**
 * The newest analyses, newest first: of the selected farm, or with 'all'
 * whatever the database access rule lets this user read.
 * Never throws: { status: 'ok', items } or { status: 'error', items: [] }.
 */
const loadRecentActivity = async (farmKey) => {
  try {
    let query = supabase
      .from(ACTIVITY_TABLE)
      .select(ACTIVITY_COLUMNS)
      .order('analyzed_at', { ascending: false })
      .limit(ACTIVITY_LIMIT);

    if (farmKey !== 'all') {
      // farm_id is a whole number; a farm id of any other kind has no rows
      if (!/^\d+$/.test(farmKey)) return { status: 'ok', items: [] };
      query = query.eq('farm_id', Number(farmKey));
    }

    const { data, error } = await query;
    if (error || !Array.isArray(data)) {
      console.warn('[IoT summary] Recent activity could not be loaded:', error?.message || 'unexpected reply');
      return { status: 'error', items: [] };
    }
    return { status: 'ok', items: data.slice(0, ACTIVITY_LIMIT).map(toActivityItem) };
  } catch (error) {
    console.warn('[IoT summary] Recent activity could not be loaded:', error?.message || error);
    return { status: 'error', items: [] };
  }
};

/**
 * @param {string|null|undefined} userId - Supabase auth id of the signed-in
 *   user (the middleware's farmerId for 'all'; a selected farm uses the
 *   farmer id of its own middleware record, see farmIdentity). Nothing is
 *   requested until it is known.
 * @param {string|number|null|undefined} selectedFarmId - A farm id; 'all',
 *   'ALL' or empty for every farm.
 * @returns {{
 *   devices: {
 *     leds:    { status: 'loading' | 'ok' | 'error', count: number | null },
 *     cameras: { status: 'loading' | 'ok' | 'error', count: number | null },
 *     sensors: { status: 'loading' | 'ok' | 'error', count: number | null }
 *   },
 *   activity: {
 *     status: 'loading' | 'ok' | 'error',
 *     items: Array<{ id: string | number, title: string, detail: string }>
 *   }
 * }} count is a number only with status 'ok'.
 */
export const useIotSummary = (userId, selectedFarmId) => {
  const farmKey = toFarmKey(selectedFarmId);
  const viewKey = userId ? `${userId}__${farmKey}` : null;

  // The device counts are read from the module cache on every render; this
  // counter only makes the component render again when the cache changes.
  const [, setCacheVersion] = useState(0);
  const [activityState, setActivityState] = useState(null); // { key, status, items }

  useEffect(() => {
    if (!userId) return undefined;
    const listener = () => setCacheVersion((version) => version + 1);
    const entry = ensureLoaded(userId, farmKey);
    entry.listeners.add(listener);
    listener(); // show what ensureLoaded() found or started
    return () => {
      entry.listeners.delete(listener);
    };
  }, [userId, farmKey]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false; // a newer user / farm took over, or the page was left
    const key = `${userId}__${farmKey}`;
    loadRecentActivity(farmKey).then((result) => {
      if (!cancelled) setActivityState({ key, ...result });
    });
    return () => {
      cancelled = true;
    };
  }, [userId, farmKey]);

  return {
    devices: readDevices(userId, farmKey),
    // Rows loaded for another user or farm are never shown for this one
    activity: activityState && activityState.key === viewKey
      ? { status: activityState.status, items: activityState.items }
      : LOADING_ACTIVITY
  };
};

export default useIotSummary;
